import {
  Box,
  HStack,
  Heading,
  LinkBox,
  LinkOverlay,
  Text,
  VStack,
  useMediaQuery
} from "@chakra-ui/react"
import IconButton, {
  Indicator
} from "@pathwright/ux/src/components/button/IconButton"
import Link from "@pathwright/ux/src/components/link/Link"
import { forwardRef, useMemo } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import {
  HomeResourceFragment,
  ResourceBackgroundType
} from "../../../api/generated"
import Pathicon from "../../../pathicon/Pathicon"
import { usePathwrightContext } from "../../../pathwright/PathwrightContext"
import { BORDER_RADIUS } from "../../constants"
import { getCertificateDownloadUrl, getRegistrationPathLink } from "../../utils"
import GroupAvatars from "../GroupAvatars"
import NestedPaths from "./NestedPaths"
import PathCardAction from "./PathCardAction"
import PathCardRegistrationControls from "./PathCardRegistrationControls"
import { PathCardProps } from "./types"

// const bgKeyframes = keyframes`
// 	0% {
// 		transform: scale(1);
// 	}
// 	10% {
// 		transform: scale(1.15);
// 	}
//   100% {
// 		transform: scale(1.2);
// 	}
// `

const CARD_BORDER_RADIUS = 25

type PathCardBackgroundProps = {
  resource: Pick<
    HomeResourceFragment,
    | "background_image"
    | "cover_image"
    | "background_type"
    | "background_overlay"
  >
}

export const PathCardBackground = ({ resource }: PathCardBackgroundProps) => {
  // When the background image is set to blur, we use the cover image as the background
  // and then overlay the cover image with the blurred background image (at a lower opacity).
  let bgImage = ""
  let bgImageOverlay = ""

  if (
    resource.background_image &&
    resource.background_type === ResourceBackgroundType.Blur
  ) {
    bgImage = resource.cover_image
    bgImageOverlay = resource.background_image
  } else {
    bgImage = resource.background_image || resource.cover_image
  }

  return (
    <Box
      pos="absolute"
      inset={0}
      borderRadius={CARD_BORDER_RADIUS}
      overflow="hidden"
    >
      <Box
        pos="absolute"
        inset={0}
        transform="scale(1.00)"
        transition="transform 8s ease-out"
        _groupHover={{
          transform: "scale(1.2)"
          // animation: `${bgKeyframes} 8s ease-out`
        }}
      >
        <Box
          pos="absolute"
          inset={0}
          backgroundImage={bgImage}
          backgroundPosition="center"
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
        />
        {!!bgImageOverlay && (
          <Box
            pos="absolute"
            inset={0}
            backgroundImage={bgImageOverlay}
            backgroundPosition="center"
            backgroundSize="cover"
            backgroundRepeat="no-repeat"
            opacity={0.0} // try without the overlay for now.
          />
        )}
        {!!resource.background_overlay && (
          <Box
            pos="absolute"
            inset={0}
            bg={`rgba(${resource.background_overlay})`}
          />
        )}
        <Box pos="absolute" inset={0} bg="blackAlpha.700" />
      </Box>
    </Box>
  )
}

const PathCard = forwardRef<HTMLDivElement, PathCardProps>(
  (
    { registration, parentRegistration, nestedRegistrations, enableLink },
    ref
  ) => {
    const resource = registration.resource!
    const pwContext = usePathwrightContext()
    // Default to enabling link.
    enableLink ??= true

    const certificateDownloadUrl = useMemo(
      () => getCertificateDownloadUrl({ pwContext, registration }),
      []
    )

    const pathLink = useMemo(() => getRegistrationPathLink(registration), [])

    const [hasHover] = useMediaQuery("(hover: hover)")

    return (
      <LinkBox
        ref={ref}
        id={`PathCard__${registration.group.id}`}
        // Using "group" className to allow revealing of PathCardRegistrationControls
        // on hover of the PathCard.
        className="PathCard group"
        w="100%"
        borderRadius={CARD_BORDER_RADIUS}
        shadow="base"
        transform="scale(1.00)"
        transition="transform .2s ease-out"
        _hover={{ shadow: "xl", transform: "scale(1.01)" }}
        sx={{
          // Conditionally layter the PathCard above siblings when it contains
          // an expanded OptionMenu, ensuring the menu is not overlaid by other
          // PathCards.
          [`&:has(.OptionMenu > button[aria-expanded='true'])`]: {
            pos: "relative",
            zIndex: 10
          }
        }}
      >
        <PathCardBackground resource={resource!} />
        <VStack
          p={8}
          borderRadius={CARD_BORDER_RADIUS}
          spacing={2}
          align="flex-start"
          justify="center"
          flexGrow={1}
          pos="relative"
        >
          {enableLink ? (
            <LinkOverlay
              className="PathCard__link-overlay"
              as={ReactRouterLink}
              to={pathLink}
              w="100%"
              textDecoration="none"
            >
              <Heading
                as="h2"
                className="theme-heading theme-heading-bold"
                size="h5"
                color="whiteAlpha.900"
                fontWeight="bold"
                noOfLines={2}
                m={0}
              >
                {resource.name}
              </Heading>
            </LinkOverlay>
          ) : (
            <Heading
              as="h2"
              className="theme-heading theme-heading-bold"
              size="h5"
              color="whiteAlpha.900"
              fontWeight="bold"
              noOfLines={2}
              m={0}
            >
              {resource.name}
            </Heading>
          )}

          {
            // Moderators and Teachers should see list of Cohort members.
            [10, 15].includes(registration.role) && (
              <GroupAvatars
                groupId={registration?.group?.id as number}
                showGroupName
              />
            )
          }

          <HStack w="100%" gap={4} sx={{ _empty: { display: "none" } }}>
            <PathCardAction registration={registration} />

            {certificateDownloadUrl && (
              <Link
                py={1}
                px={4}
                borderRadius={BORDER_RADIUS}
                _hover={{
                  textDecoration: "none",
                  bg: "blackAlpha.600",
                  shadow: "md"
                }}
                textDecoration="none"
                color="whiteAlpha.900"
                href={certificateDownloadUrl}
                download
              >
                <HStack>
                  <Pathicon icon="certificate-fill" />
                  <Text
                    m={0}
                    fontSize="sm"
                    fontWeight="bold"
                    color="whiteAlpha.900"
                  >
                    Certificate
                  </Text>
                </HStack>
              </Link>
            )}
          </HStack>
          <NestedPaths nestedRegistrations={nestedRegistrations} />
        </VStack>
        <Box pos="absolute" right={2.5} top={2}>
          <HStack>
            {!!registration.pendingReviewCount && (
              <Box
                // Show on hover / always show on mobile.
                opacity={hasHover ? 0 : 1}
                _groupHover={{ display: "inline-flex", opacity: 1 }}
                transition="opacity .2s ease"
              >
                <IconButton
                  as={Link as ReactRouterLink}
                  aria-label="Inbox Pending Review Count"
                  icon="inbox"
                  roundSize={6}
                  indicator
                  variant="solid"
                  colorScheme="whiteAlpha"
                  to={`/inbox?filter=pending&order=asc&cohort_id=${registration.group.id}&ui_selected_parent=${registration.group.id}`}
                />
              </Box>
            )}
            <PathCardRegistrationControls
              registration={registration}
              parentRegistration={parentRegistration}
              // NOTE: rendering portal to overlay toolbar. Hopefully the UX tradeoff
              // is nominal, but this does mean the menu is slightly jerkey when scrolling.
              renderPortal
              renderOnGroupHover={!registration.pendingReviewCount}
            />
          </HStack>
        </Box>
        {!!registration.pendingReviewCount && (
          <Indicator
            // Hide on hover / never show on mobile.
            opacity={hasHover ? 1 : 0}
            _groupHover={{ opacity: 0 }}
            transition="opacity .2s ease"
          />
        )}
      </LinkBox>
    )
  }
)

export default PathCard
